import dayjs, { Dayjs } from "dayjs";
import utc from "dayjs/plugin/utc";
import { minBy } from "helpers";
import {
  ReviewPeriodData,
  ReviewSlotRevision,
} from "src/store/api/generatedApi";
import {
  eventDiffersFromBaseline,
  ScrapEyesEventWithReviewData,
} from "../contexts/ManualReviewEventContext";
import {
  BringInPeriodRequest,
  BringInPeriodResult,
} from "../LinkedPeriodBringInDialog";
import {
  BringInProgress,
  broughtInPeriodWillVerify,
  firstCrossingNeighbour,
  linkedPeriodFromCrossingPair,
  MAX_CHAIN_HOPS,
  offWindowPeriodsEditedInFocus,
  periodOverlapsFocus,
  periodsToPersist,
  periodSliceFromFocus,
  periodWithinFocus,
  resolvePairChain,
} from "./editScope";

/**
 * `resolveBringInChain`'s outcome: the resolved periods and events, or `capped` (see
 * `resolvePairChain`) — passed straight through without touching the capture/persist
 * bookkeeping below.
 */
export type BringInChainResult =
  | {
      outcome: "resolved";
      periods: ReviewPeriodData[];
      broughtInEvents: ScrapEyesEventWithReviewData[];
      broughtInOriginalEvents: ScrapEyesEventWithReviewData[];
      reviewSlotRevisions?: ReviewSlotRevision[];
    }
  | { outcome: "capped" };

dayjs.extend(utc);

// A bring-in hop shows the crossing region — the target period flanked by one
// period's width on each side — so both ends of the pair are visible with video.
// A target outside the loaded window is fetched into the modal's own context.
export const buildBringInRequest = (
  period: ReviewPeriodData,
  dayPeriods: ReviewPeriodData[],
  windowStart: Dayjs,
  windowEnd: Dayjs,
  progress: BringInProgress
): BringInPeriodRequest => {
  // Parse in UTC so the modal's video timeline labels the crossing region in UTC,
  // matching the page; a local-mode Dayjs would render the ticks an offset out.
  const start = dayjs.utc(period.start);
  const end = dayjs.utc(period.end);
  const flank = end.diff(start, "second");
  const focusStart = start.subtract(flank, "second");
  const focusEnd = end.add(flank, "second");
  const reviewPeriods = dayPeriods.filter(
    (candidate) =>
      dayjs.utc(candidate.start).isBefore(focusEnd) &&
      dayjs.utc(candidate.end).isAfter(focusStart)
  );
  const outOfWindow = start.isBefore(windowStart) || !start.isBefore(windowEnd);
  return {
    targetPeriod: period,
    focusStart,
    focusEnd,
    windowStart,
    windowEnd,
    reviewPeriods,
    outOfWindow,
    progress,
  };
};

/**
 * Resolves the chain of off-window periods a save must verify together with its seeds.
 * A lift/drop pair can reach past the window edge into a review period the reviewer
 * can't see. When an edit affects that pair, the other end must be reviewed so the
 * pair stays consistent; an unaffected pair links nothing. Starting from the periods
 * a save already touches, the chain opens the bring-in modal one hop at a time so the
 * reviewer can adjust the crossing region and pull each such off-window period in; a
 * brought-in period, or an edit made in the modal, may reveal a further link. Periods
 * already in view are left alone — their events are visible and verified in place, so
 * a pair crossing between them needs no bring-in. Each off-window period supplies its
 * complete event set from the modal, since the page context does not hold it. Returns
 * every period to verify together plus those event sets.
 */
export const resolveBringInChain = async ({
  seedPeriods,
  maxSequenceGapSeconds,
  allReviewPeriods,
  windowStart,
  windowEnd,
  seedFocusEvents,
  bringInPeriod,
}: {
  seedPeriods: ReviewPeriodData[];
  maxSequenceGapSeconds: number;
  allReviewPeriods: ReviewPeriodData[];
  windowStart: Dayjs;
  windowEnd: Dayjs;
  seedFocusEvents: () => {
    events: ScrapEyesEventWithReviewData[];
    originalEvents: ScrapEyesEventWithReviewData[];
  };
  bringInPeriod: (
    request: BringInPeriodRequest
  ) => Promise<BringInPeriodResult>;
}): Promise<BringInChainResult> => {
  // Keyed by period id so stepping back and re-deciding overwrites, rather than
  // double-counts, a period's captured events — including a seed's own passive
  // capture from whichever focus reaches it; only periods left in the final
  // scope contribute to the payload.
  let revisionSnapshots: ReviewSlotRevision[][] = [];
  const capturedByPeriodId = new Map<
    number,
    {
      events: ScrapEyesEventWithReviewData[];
      originalEvents: ScrapEyesEventWithReviewData[];
      edited: boolean;
      // Every event across the whole focus window the period was shown in. A period's
      // onward crossing is detected from this one loaded set, never a re-match across
      // the page/brought-in seam.
      focusEvents: ScrapEyesEventWithReviewData[];
      // That focus window's stored events. An onward crossing links only through a
      // sequence affected by an edit against these, carried edits included.
      focusOriginalEvents: ScrapEyesEventWithReviewData[];
      // That focus window's seed (fetched overlaid with carried edits). Diffing
      // `focusEvents` against it finds the periods the page edited — including a
      // visible neighbour no crossing pair reaches, which the edit alone brings in.
      focusSeedEventsById: Map<string, ScrapEyesEventWithReviewData>;
    }
  >();

  // The focus window of the most recently opened page. A later hop reaching a period
  // shown in full on it is auto-included from these edits — no page of its own — while
  // still able to bring in its own further neighbours. `seedEventsById` is that page's
  // seed, the baseline for what counts as edited on it.
  let lastFocus: {
    start: Dayjs;
    end: Dayjs;
    events: ScrapEyesEventWithReviewData[];
    originalEvents: ScrapEyesEventWithReviewData[];
    seedEventsById: Map<string, ScrapEyesEventWithReviewData>;
  } | null = null;

  const periodStart = (period: ReviewPeriodData) => dayjs(period.start);
  const periodEnd = (period: ReviewPeriodData) => dayjs(period.end);
  const periodId = (period: ReviewPeriodData) => period.id;

  // A seed never goes through `bringInPeriod` itself, so without this, an edit made
  // to it while visible in a neighbour's focus would never be captured. Tracked apart
  // from `capturedByPeriodId` — which a seed also gains a passive entry in — so a
  // captured seed is never mistaken for one that stopped being a seed.
  const trueSeedIds = new Set(seedPeriods.map(periodId));

  // A true seed's own slice of a hop's focus, captured passively whenever the seed's
  // range overlaps that focus — gated on overlap so a later hop whose focus has moved
  // past the seed leaves an earlier, genuine capture untouched.
  const captureSeedSlices = ({
    focusStart,
    focusEnd,
    focusEvents,
    focusOriginalEvents,
    focusSeedEventsById,
  }: {
    focusStart: Dayjs;
    focusEnd: Dayjs;
    focusEvents: ScrapEyesEventWithReviewData[];
    focusOriginalEvents: ScrapEyesEventWithReviewData[];
    focusSeedEventsById: Map<string, ScrapEyesEventWithReviewData>;
  }): void => {
    seedPeriods
      .filter((seed) =>
        periodOverlapsFocus({
          periodStart: periodStart(seed),
          periodEnd: periodEnd(seed),
          focusStart,
          focusEnd,
        })
      )
      .forEach((seed) => {
        capturedByPeriodId.set(periodId(seed), {
          ...periodSliceFromFocus({
            periodStart: periodStart(seed),
            periodEnd: periodEnd(seed),
            focusEvents,
            focusOriginalEvents,
            focusSeedEventsById,
            eventDiffersFromSeed: eventDiffersFromBaseline,
            previouslyEdited:
              capturedByPeriodId.get(periodId(seed))?.edited ?? false,
          }),
          focusEvents,
          focusOriginalEvents,
          focusSeedEventsById,
        });
      });
  };

  const chainResult = await resolvePairChain<ReviewPeriodData>({
    seedPeriodIds: seedPeriods.map((period) => period.id),
    periodId,
    findNextLinked: ({ scopePeriodIds, resolvedPeriodIds }) => {
      // Three independent, single-namespace detections, each pinned so the walk can
      // never march past a real crossing:
      //  (1) a seed (in-view) period whose own affected lift/drop sequence reaches past
      //      its real boundary into a neighbour, detected from the page's own events;
      //  (2) a brought-in neighbour this save will verify reaching its own neighbour
      //      through an affected sequence, detected from that period's own focus events;
      //  (3) an off-window period whose own events a page edited — the edit marks it
      //      for re-verification, so it joins the chain with or without a crossing.
      // A neighbour extends only when this save verifies it, so the walk follows a
      // run of periods it verifies and stops at an untouched already-verified one.
      const seedPeriodIds = new Set(
        [...scopePeriodIds].filter((id) => trueSeedIds.has(id))
      );
      const seedEvents = seedFocusEvents();
      const seedLink = firstCrossingNeighbour({
        focusEvents: seedEvents.events,
        focusOriginalEvents: seedEvents.originalEvents,
        eventDiffersFromOriginal: eventDiffersFromBaseline,
        maxSequenceGapSeconds,
        dayPeriods: allReviewPeriods,
        windowStart,
        windowEnd,
        periodStart,
        periodEnd,
        periodId,
        originPeriodIds: seedPeriodIds,
        scopePeriodIds,
        resolvedPeriodIds,
      });

      const verifiedNeighbourLinks = [...capturedByPeriodId]
        .filter(([id, captured]) => {
          if (!scopePeriodIds.has(id)) return false;
          const neighbour = allReviewPeriods.find(
            (period) => periodId(period) === id
          );
          return (
            neighbour !== undefined &&
            broughtInPeriodWillVerify({
              isVerified: neighbour.verified_at != null,
              edited: captured.edited,
            })
          );
        })
        .map(([id, captured]) =>
          linkedPeriodFromCrossingPair({
            focusEvents: captured.focusEvents,
            focusOriginalEvents: captured.focusOriginalEvents,
            eventDiffersFromOriginal: eventDiffersFromBaseline,
            maxSequenceGapSeconds,
            dayPeriods: allReviewPeriods,
            windowStart,
            windowEnd,
            periodStart,
            periodEnd,
            periodId,
            originPeriodId: id,
            scopePeriodIds,
            resolvedPeriodIds,
          })
        );

      const editedPeriodLinks = [...capturedByPeriodId]
        .filter(([id]) => scopePeriodIds.has(id))
        .flatMap(([, captured]) =>
          offWindowPeriodsEditedInFocus({
            focusEvents: captured.focusEvents,
            focusSeedEvents: [...captured.focusSeedEventsById.values()],
            eventDiffersFromSeed: eventDiffersFromBaseline,
            dayPeriods: allReviewPeriods,
            windowStart,
            windowEnd,
            periodStart,
            periodEnd,
            periodId,
            scopePeriodIds,
            resolvedPeriodIds,
          })
        );

      return minBy(
        [seedLink, ...verifiedNeighbourLinks, ...editedPeriodLinks].filter(
          (period): period is ReviewPeriodData => period !== undefined
        ),
        (period) => periodStart(period).valueOf()
      );
    },
    bringInPeriod: async (period, progress, acceptedPeriodIds) => {
      const start = periodStart(period);
      const end = periodEnd(period);
      // Whether some page of this chain already edited this period's events. Each
      // accepted capture is diffed against its own page's seed, so an edit keeps
      // counting however many pages later the period is reached, while edits merely
      // carried into a page's seed never count as made there.
      const editedOnEarlierPage = [...capturedByPeriodId]
        .filter(([id]) => acceptedPeriodIds.has(id))
        .some(
          ([, captured]) =>
            offWindowPeriodsEditedInFocus({
              focusEvents: captured.focusEvents,
              focusSeedEvents: [...captured.focusSeedEventsById.values()],
              eventDiffersFromSeed: eventDiffersFromBaseline,
              dayPeriods: [period],
              windowStart,
              windowEnd,
              periodStart,
              periodEnd,
              periodId,
              scopePeriodIds: new Set<number>(),
              resolvedPeriodIds: new Set<number>(),
            }).length > 0
        );
      // An edited-status already recorded for this period survives a re-capture: the
      // re-shown page is seeded with those very edits, so its own diff reads clean.
      const previouslyEdited =
        capturedByPeriodId.get(period.id)?.edited ?? false;
      // A period shown in full on the previous page is verified from those same edits
      // without a page of its own; if it is unverified or was edited it still brings in
      // its own further neighbours (as context on later pages). A hop that was shown
      // as a page reopens as one, so a step back always lands on a page.
      if (
        progress.canBypassPage &&
        lastFocus !== null &&
        periodWithinFocus({
          periodStart: start,
          periodEnd: end,
          focusStart: lastFocus.start,
          focusEnd: lastFocus.end,
        })
      ) {
        capturedByPeriodId.set(period.id, {
          ...periodSliceFromFocus({
            periodStart: start,
            periodEnd: end,
            focusEvents: lastFocus.events,
            focusOriginalEvents: lastFocus.originalEvents,
            focusSeedEventsById: lastFocus.seedEventsById,
            eventDiffersFromSeed: eventDiffersFromBaseline,
            previouslyEdited: editedOnEarlierPage || previouslyEdited,
          }),
          focusEvents: lastFocus.events,
          focusOriginalEvents: lastFocus.originalEvents,
          focusSeedEventsById: lastFocus.seedEventsById,
        });
        captureSeedSlices({
          focusStart: lastFocus.start,
          focusEnd: lastFocus.end,
          focusEvents: lastFocus.events,
          focusOriginalEvents: lastFocus.originalEvents,
          focusSeedEventsById: lastFocus.seedEventsById,
        });
        return "inWithoutPage";
      }
      // Only periods still accepted contribute — a period declined on a step back
      // keeps a captured entry, but it is neither seeded nor marked here. The current
      // period is kept so a step back onto it reopens with its own edits.
      const inScopeCaptures = [...capturedByPeriodId].filter(
        ([id]) => acceptedPeriodIds.has(id) || id === period.id
      );
      // Seed the modal with edits from periods already brought in this chain, so a
      // neighbour's focus (which overlaps them) shows their edits rather than the
      // re-fetched originals, and stepping back to a period reopens it with its edits.
      const carriedEvents = inScopeCaptures.flatMap(
        ([, captured]) => captured.events
      );
      const carriedOriginalEvents = inScopeCaptures.flatMap(
        ([, captured]) => captured.originalEvents
      );
      // The periods this save will verify, so the modal marks them "will verify"
      // instead of fading the in-view seeds it shows as context: seeds, brought-in
      // periods per broughtInPeriodWillVerify, and the target when it is unverified
      // (a verified target turns amber once the reviewer edits it).
      const willVerifyPeriodIds = [
        ...seedPeriods.map((seed) => seed.id),
        ...inScopeCaptures
          .filter(([id, captured]) => {
            if (!acceptedPeriodIds.has(id)) return false;
            const broughtIn = allReviewPeriods.find(
              (candidate) => candidate.id === id
            );
            return (
              broughtIn !== undefined &&
              broughtInPeriodWillVerify({
                isVerified: broughtIn.verified_at != null,
                edited: captured.edited,
              })
            );
          })
          .map(([id]) => id),
        ...(period.verified_at == null ? [period.id] : []),
      ];
      const request = {
        ...buildBringInRequest(
          period,
          allReviewPeriods,
          windowStart,
          windowEnd,
          progress
        ),
        carriedEvents,
        carriedOriginalEvents,
        willVerifyPeriodIds,
      };
      const result = await bringInPeriod(request);
      if (result.decision === "in" && request.outOfWindow) {
        if (result.reviewSlotRevisions !== undefined)
          revisionSnapshots = [
            ...revisionSnapshots,
            result.reviewSlotRevisions,
          ];
        const focusEvents = result.focusEvents ?? [];
        const focusOriginalEvents = result.focusOriginalEvents ?? [];
        const focusSeedEventsById =
          result.focusSeedEventsById ??
          new Map<string, ScrapEyesEventWithReviewData>();
        capturedByPeriodId.set(period.id, {
          ...periodSliceFromFocus({
            periodStart: start,
            periodEnd: end,
            focusEvents,
            focusOriginalEvents,
            focusSeedEventsById,
            eventDiffersFromSeed: eventDiffersFromBaseline,
            previouslyEdited: editedOnEarlierPage || previouslyEdited,
          }),
          focusEvents,
          focusOriginalEvents,
          focusSeedEventsById,
        });
        captureSeedSlices({
          focusStart: request.focusStart,
          focusEnd: request.focusEnd,
          focusEvents,
          focusOriginalEvents,
          focusSeedEventsById,
        });
        lastFocus = {
          start: request.focusStart,
          end: request.focusEnd,
          events: focusEvents,
          originalEvents: focusOriginalEvents,
          seedEventsById: focusSeedEventsById,
        };
      }
      return result.decision;
    },
    maxHops: MAX_CHAIN_HOPS,
  });

  if (chainResult.outcome === "capped") {
    return { outcome: "capped" };
  }
  const periodIds = chainResult.scope;

  // An already-verified neighbour brought in but left unedited is dropped here so
  // it keeps its existing generation instead of being superseded into an identical
  // one; its events go too, since the save only carries events for periods it verifies.
  // A seed is excluded entirely: always kept regardless of edit state, so its passive
  // capture must never feed this rule.
  const broughtInEdited = new Map(
    [...capturedByPeriodId]
      .filter(([id]) => !trueSeedIds.has(id))
      .map(([id, captured]) => [id, captured.edited])
  );
  const periods = periodsToPersist({
    periods: allReviewPeriods.filter((period) => periodIds.has(period.id)),
    isVerified: (period) => period.verified_at != null,
    periodId: (period) => period.id,
    broughtInEdited,
  });
  const persistedIds = new Set(periods.map((period) => period.id));
  return {
    outcome: "resolved",
    reviewSlotRevisions:
      revisionSnapshots.length === 0 ? undefined : revisionSnapshots.flat(),
    periods,
    broughtInEvents: [...persistedIds].flatMap(
      (id) => capturedByPeriodId.get(id)?.events ?? []
    ),
    broughtInOriginalEvents: [...persistedIds].flatMap(
      (id) => capturedByPeriodId.get(id)?.originalEvents ?? []
    ),
  };
};
